import React, {useContext, useEffect, useState} from 'react';
import {Link, useNavigate} from 'react-router-dom';
import {Button, Offcanvas} from '../../components/ui';
import {DragDropContext} from "react-beautiful-dnd";
import {useParams} from "react-router-dom";
import useApi from "../../hooks/useApi/useApi";
import {ITaskList, ITask} from "../../ModelContracts";
import CreateTask from "../../components/CreateTask/CreateTask";
import TaskList from "../../components/TaskList/TaskList";
import {useMutation, useQuery, useQueryClient} from "react-query";
import ViewTask from "../../components/ViewTask/ViewTask";
import BoardTaskListChart from "../../components/BoardTaskListChart/BoardTaskListChart";
import BoardProgressChart from "../../components/BoardProgressChart/BoardProgressChart";
import Labels from "../../components/Labels/Labels";
import RecentBoardEvents from "../../components/RecentBoardEvents/RecentBoardEvents";
import BoardChannel from "../../components/BoardChannel/BoardChannel";
import UserList from "../../components/UserList/UserList";
import BoardMembers from "../../components/BoardMembers/BoardMembers";
import {BoardContext, BoardProvider} from "../../contexts/BoardContext";

import './Board.css';
import '../../styles/SideBar.css';
import {TaskProvider} from "../../contexts/TaskContext";

interface IMoveTaskModel {
  boardId: string,
  taskId: string,
  targetListId: string,
  index: number
}

interface IProps {
  showChannel?: boolean,
}

function Board({showChannel}: IProps) {
  const {boardId, taskId} = useParams() as { boardId: string, taskId?: string };
  const navigate = useNavigate();
  const {board, isAdmin} = useContext(BoardContext);
  const {labels} = board;
  const [taskLists, setTaskLists] = useState<Array<ITaskList>>(board?.taskLists);
  const [isCreating, setIsCreating] = useState(false);
  const [isAddingMember, setIsAddingMember] = useState(false);

  useEffect(() => {
    setTaskLists(board.taskLists);
  }, [board]);

  const {moveTask, archiveBoard, reactivateBoard, getAvailableOnboardings} = useApi();

  const queryClient = useQueryClient();

  const moveTaskCommand =
    useMutation(({boardId, taskId, targetListId, index}: IMoveTaskModel) =>
      moveTask(boardId, taskId, targetListId, index), {
      onSuccess: async () => {
        await queryClient.invalidateQueries('board');
        await queryClient.invalidateQueries('board-events');
      }
    });

  const archiveBoardCommand = useMutation(() => archiveBoard(boardId), {
    onSuccess: async () => {
      await queryClient.invalidateQueries('board');
      await queryClient.invalidateQueries('available-onboardings');
      await queryClient.invalidateQueries('board-events');
    }
  });

  const reactiveBoardCommand = useMutation(() => reactivateBoard(boardId), {
    onSuccess: async () => {
      await queryClient.invalidateQueries('board');
      await queryClient.invalidateQueries('available-onboardings');
      await queryClient.invalidateQueries('board-events');
    }
  });

  const {data: availableOnboardings} =
    useQuery(['available-onboardings'], () => getAvailableOnboardings());

  const handleStartCreating = () => setIsCreating(true);
  const handleStopCreating = () => setIsCreating(false);
  const handleStopEditing = () => {
    navigate(`/boards/${boardId}`);
  };
  const handleCloseChannel = () => {
    navigate(`/boards/${boardId}`);
  };
  const handleStartAddingMember = () => setIsAddingMember(true);
  const handleStopAddingMember = () => setIsAddingMember(false);
  const handleStopDragging = async (result: any) => {
    const {source, destination, draggableId} = result;
    const sourceList = taskLists.find((tl: ITaskList) => tl.id === source.droppableId) as any;
    const targetList = taskLists.find((tl: ITaskList) => tl.id === destination.droppableId) as any;
    const tasks = taskLists.flatMap((tl: ITaskList) => tl.tasks);
    const task = tasks.find((t: ITask) => t.id === draggableId) as ITask;
    const getTasks = (list: ITaskList) => {
      const tasks = [...list.tasks];
      if (list === sourceList) tasks.splice(source.index, 1);
      if (list === targetList) tasks.splice(destination.index, 0, task);
      return tasks;
    };
    const updatedTaskLists = taskLists.map((tl: any) => {
      return {
        ...tl,
        tasks: getTasks(tl)
      }
    }) as any;
    setTaskLists(updatedTaskLists);
    moveTaskCommand.mutate({
      boardId,
      taskId: task.id,
      targetListId: targetList.id,
      index: destination.index
    });
  };

  const hasTasks = () => taskLists.flatMap((tl: ITaskList) => tl.tasks).length > 0;

  const handleArchive = () => {
    archiveBoardCommand.mutate();
  };

  const handleReactivate = () => {
    reactiveBoardCommand.mutate();
  };

  return (
    <DragDropContext onDragEnd={handleStopDragging}>
      <div className="board-container relative -ml-3">
      <div className="w-full -mt-4 mb-4">
          <BoardTaskListChart taskLists={taskLists}/>
        </div>
        <div className="w-full px-3">
          <div className="flex flex-wrap items-start board-header justify-between">
            <div className="flex items-center my-2 w-5/12" aria-label="Board Header">
              <div className="flex flex-col ml-4">
                <h3 className="mr-6 mb-0">{board.name}</h3>
                <Labels labels={labels} maxCharacterCount={30}/>
              </div>
            </div>
            <div className="flex items-center my-2 justify-end w-3/12"
                 aria-label="Board Member List">
              {
                board.members &&
                  <UserList users={board.members} avatarSize={32} maxCount={5}/>
              }
            </div>
            {
              isAdmin &&
                <div className="flex my-2 justify-end items-center gap-2 flex-1 min-w-0">
                    <Button variant="secondary" onClick={handleArchive}>Archive</Button>
                    <Button variant="secondary" onClick={handleStartAddingMember}>Add Member</Button>
                    <Button onClick={handleStartCreating}>Create Task</Button>
                </div>
            }
          </div>
          <div className="flex flex-wrap mt-2">
            {
              taskLists.map((taskList: ITaskList, i: number) => (
                <div key={taskList.id}
                     className="w-full md:w-3/12 px-3 mb-4 md:mb-0">
                  <TaskList
                    taskList={taskList}
                    emptyText={
                      i === 0 && !hasTasks() ?
                        "Nothing to show here. Create a task to get started." :
                        ""
                    }
                    onAdded={
                      i === 0 ? handleStartCreating : undefined
                    }
                    isAdmin={isAdmin}
                  />
                </div>
              ))
            }
            <div className="w-full md:w-3/12 px-3">
              <RecentBoardEvents boardId={boardId}/>
            </div>
          </div>
          <Offcanvas
            show={isCreating}
            onHide={handleStopCreating}
            placement="end"
            className="create-task side-bar"
            aria-label="Create task">
            <Offcanvas.Header closeButton>
              <Offcanvas.Title role="heading">Create Task</Offcanvas.Title>
            </Offcanvas.Header>
            <Offcanvas.Body>
              <CreateTask
                onCreate={handleStopCreating}
                boardId={boardId}
              />
            </Offcanvas.Body>
          </Offcanvas>
          <Offcanvas show={Boolean(taskId)}
                     onHide={handleStopEditing}
                     placement="end"
                     className="edit-board-task side-bar">
            <Offcanvas.Header closeButton className="flex flex-row-reverse">
            </Offcanvas.Header>
            <Offcanvas.Body>
              <TaskProvider>
                <ViewTask/>
              </TaskProvider>
            </Offcanvas.Body>
          </Offcanvas>
          <Offcanvas show={showChannel}
                     onHide={handleCloseChannel}
                     placement="end"
                     className="view-board-channel">
            <Offcanvas.Header closeButton>
              <Offcanvas.Title role="heading">Board Events</Offcanvas.Title>
            </Offcanvas.Header>
            <Offcanvas.Body>
              <BoardChannel boardId={boardId}/>
            </Offcanvas.Body>
          </Offcanvas>
          <Offcanvas aria-label="Board Members"
                     placement="end"
                     show={isAddingMember}
                     className="side-bar"
                     onHide={handleStopAddingMember}>
            <Offcanvas.Header closeButton>
              <Offcanvas.Title role="heading">Board Members</Offcanvas.Title>
            </Offcanvas.Header>
            <Offcanvas.Body>
              <BoardMembers board={board}/>
            </Offcanvas.Body>
          </Offcanvas>
        </div>
        <div className="w-full">
          <BoardProgressChart tasks={taskLists.flatMap(tl => tl.tasks)}/>
        </div>
        {
          board.isArchived &&
            <div className="w-full archived-board">
                <div className="flex flex-col justify-center items-center">
                    <div className="card bg-base-100 shadow-sm border border-base-300 reactivate-board text-center p-2"
                         role="dialog"
                         aria-label="Reactivate">
                        <div className="card-body flex justify-center items-center flex-col">
                            <h2 className="mb-6">Archived</h2>
                          {
                            Boolean(availableOnboardings) &&
                              <Button disabled={!availableOnboardings?.moreAvailable}
                                      onClick={handleReactivate}>Reactivate</Button>
                          }
                          {
                            !availableOnboardings?.moreAvailable &&
                              <p className="text-sm italic px-4 mt-4">
                                  <Link to={"/plan"}
                                        className="underline">
                                      Upgrade plan</Link> or archive a different customer to reactivate
                              </p>
                          }
                        </div>
                    </div>
                </div>
            </div>
        }
      </div>
    </DragDropContext>
  );
}

function BoardWithData({showChannel}: IProps) {
  return (
    <BoardProvider>
      <Board showChannel={showChannel}/>
    </BoardProvider>
  );
}

export default BoardWithData;
